import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Mention from '@tiptap/extension-mention';
import { Popover, Button } from 'antd';
import { useStore } from '../../inbox-store';
import { useLinkShortener } from './useLinkShortener';
import { LinkShortenerExtension } from './LinkShortenerExtension';
import { createMentionSuggestion, MentionItem } from './MentionSuggestion';
import human from 'humanparser';
import session from '../../../../utils/session';

import './MentionSuggestion.css';

type Props = {
  message: string;
  setMessage: (m: string) => void;
  activeContact: any;
  activeContactModel?: any;
  isSending: boolean;
  onProcessingChange?: (isProcessing: boolean) => void;
  excludedClearTrigger?: number;
};

const MessageRichEditor: React.FC<Props> = ({ message, setMessage, activeContact, activeContactModel, isSending, onProcessingChange, excludedClearTrigger }) => {
  const [linkPopoverOpen, setLinkPopoverOpen] = useState(false);

  // Use a ref to store current mention items so the suggestion can always access the latest
  const mentionItemsRef = useRef<MentionItem[]>([]);

  // Build mention items based on active contact and user data
  const mentionItems: MentionItem[] = useMemo(() => {
    const userData = session.get('userData');
    const contact = activeContactModel?.name ? human.parseName(activeContactModel.name) : { firstName: '', lastName: '' };
    
    const items: MentionItem[] = [
      {
        id: 'firstname',
        value: `${contact.firstName || '{{firstname}}'}`,
        label: contact.firstName || '(no first name)',
        category: 'Firstname',
      },
      {
        id: 'lastname',
        value: `${contact.lastName || '{{lastname}}'}`,
        label: contact.lastName || '(no last name)',
        category: 'Lastname',
      },
      {
        id: 'recruiter',
        value: `${userData?.name || '{{recruiter}}'}`,
        label: userData?.name || 'Recruiter',
        category: 'Recruiter',
      },
      {
        id: 'brand',
        value: `${userData?.companyName || '{{brand}}'}`,
        label: userData?.companyName || 'Brand',
        category: 'Brand',
      },
    ];
    
    // Update the ref so suggestion always has latest items
    mentionItemsRef.current = items;
    return items;
  }, [activeContactModel]);
  const [linkPopoverUrl, setLinkPopoverUrl] = useState('');
  const [linkPopoverCoords, setLinkPopoverCoords] = useState<{ x: number; y: number } | null>(null);
  const [activeLinkElement, setActiveLinkElement] = useState<HTMLAnchorElement | null>(null);
  const [shorteningLoading, setShorteningLoading] = useState(false);
  const [linkPos, setLinkPos] = useState<number | null>(null);
  const [linkTo, setLinkTo] = useState<number | null>(null);
  const popupAnchorRef = useRef<HTMLSpanElement | null>(null);

  // State for shortened link popup
  const [shortenedPopoverOpen, setShortenedPopoverOpen] = useState(false);
  const [shortenedOriginalUrl, setShortenedOriginalUrl] = useState('');
  const [shortenedPopoverCoords, setShortenedPopoverCoords] = useState<{ x: number; y: number } | null>(null);
  const [shortenedLinkPos, setShortenedLinkPos] = useState<number | null>(null);
  const [shortenedLinkTo, setShortenedLinkTo] = useState<number | null>(null);

  const isValidUrl = (text: string) => {
    const pattern = /^(https?:\/\/)?[a-zA-Z0-9][-a-zA-Z0-9]*(\.[a-zA-Z]{2,})+(:\d+)?(\/[^\s]*)?$/i;
    return pattern.test(text);
  };

  const validateAndUpdateLinks = (editorInstance: any) => {
    if (!editorInstance) return;
    const { state, view } = editorInstance;
    const { doc, tr } = state;
    let hasChanges = false;
    doc.descendants((node: any, pos: number) => {
      if (node.isText) {
        const linkMark = node.marks.find((m: any) => m.type.name === 'link');
        if (linkMark) {
          const text = node.text || '';
          // Don't remove processing links
          if (linkMark.attrs.processing) return;
          if (!isValidUrl(text)) {
            const from = pos;
            const to = pos + node.nodeSize;
            tr.removeMark(from, to, linkMark.type);
            hasChanges = true;
          }
        }
      }
    });
    if (hasChanges) view.dispatch(tr);
  };

  const updatePopoverFromActiveLink = () => {
    if (activeLinkElement && linkPopoverOpen) {
      if (!document.body.contains(activeLinkElement)) {
        setLinkPopoverOpen(false);
        setLinkPopoverCoords(null);
        setActiveLinkElement(null);
        return;
      }
      const currentText = activeLinkElement.textContent || '';
      if (!isValidUrl(currentText)) {
        setLinkPopoverOpen(false);
        setLinkPopoverCoords(null);
        setActiveLinkElement(null);
        return;
      }
      const fullUrl = currentText.startsWith('http') ? currentText : `https://${currentText}`;
      activeLinkElement.setAttribute('data-href', fullUrl);
      if (fullUrl !== linkPopoverUrl) setLinkPopoverUrl(fullUrl);
      const rect = activeLinkElement.getBoundingClientRect();
      setLinkPopoverCoords({ x: rect.left + rect.width / 2 + window.scrollX, y: rect.top + window.scrollY });
    }
  };

  const shortenUrlFromStore = useStore((s: any) => s.shortenUrl);

  // Create suggestion config once, using a getter to always access latest items from ref
  const mentionSuggestion = useMemo(() => createMentionSuggestion(() => mentionItemsRef.current), []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Disable all formatting features not needed for plain text with mentions/links
        bold: false,
        italic: false,
        strike: false,
        code: false,
        heading: false,
        blockquote: false,
        codeBlock: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        horizontalRule: false,
        dropcursor: false,
        gapcursor: false,
        // Keep only essential features
        // document: true, (always included)
        // text: true, (always included)
        // paragraph: true, (needed for multi-line)
        // history: true, (undo/redo)
        // hardBreak: true, (line breaks)
      }),
      Placeholder.configure({ placeholder: 'input @ to insert mail-merge fields', showOnlyWhenEditable: false }),
      (LinkShortenerExtension as any).configure({
        shortenUrl: shortenUrlFromStore,
        onProcessingChange,
        excludedClearTrigger,
      }),
      Mention.configure({
        HTMLAttributes: {
          class: 'mention',
        },
        suggestion: mentionSuggestion,
        renderText({ node }) {
          return node.attrs.id;
        },
        renderHTML({ node }) {
          return node.attrs.id; // Render as plain text
        },
      }),
    ],
    content: message,
    onUpdate: ({ editor }) => {
      setMessage(editor.getText({ blockSeparator: '\n' }));
      setTimeout(() => {
        validateAndUpdateLinks(editor);
        updatePopoverFromActiveLink();
      }, 100);
    },
    onFocus: () => {},
    onBlur: () => {},
    editable: !!activeContact && !isSending,
    editorProps: {
      handleKeyDown(view, event) {
        if (event.key === 'Backspace' || event.key === 'Delete') {
          const { state } = view;
          const { selection } = state;
          const { from, to } = selection;

          // If cursor/selection touches a shortened link, delete the whole link atomically
          let shortenedRange: { from: number; to: number } | null = null;
          state.doc.nodesBetween(Math.max(0, from - 1), Math.min(state.doc.content.size, to + 1), (node, pos) => {
            if (node.isText) {
              const linkMark = node.marks.find((m: any) => m.type.name === 'link' && m.attrs && m.attrs.shortened);
              if (linkMark) {
                const markType = linkMark.type;
                let start = pos;
                let end = pos + node.nodeSize;
                while (start > 0 && markType.isInSet(state.doc.resolve(start - 1).marks())) {
                  start--;
                }
                while (end < state.doc.content.size && markType.isInSet(state.doc.resolve(end).marks())) {
                  end++;
                }
                shortenedRange = { from: start, to: end };
                return false;
              }
            }
          });

          if (shortenedRange) {
            event.preventDefault();
            // Close any open popovers and clear related state so UI stays in sync
            try {
              setShortenedPopoverOpen(false);
            } catch (e) {}
            try {
              setLinkPopoverOpen(false);
            } catch (e) {}
            try {
              setActiveLinkElement(null);
            } catch (e) {}
            try {
              setShortenedOriginalUrl('');
            } catch (e) {}
            try {
              setShortenedLinkPos(null);
            } catch (e) {}
            try {
              setShortenedLinkTo(null);
            } catch (e) {}
            try {
              setLinkPos(null);
            } catch (e) {}
            try {
              setLinkTo(null);
            } catch (e) {}
            try {
              setLinkPopoverUrl('');
            } catch (e) {}

            const tr = state.tr.delete(shortenedRange.from, shortenedRange.to);
            view.dispatch(tr.scrollIntoView());
            return true;
          }

          // Prevent deletion of processing links (existing behavior)
          let hasProcessingLink = false;
          state.doc.nodesBetween(Math.max(0, from - 1), Math.min(state.doc.content.size, to + 1), (node) => {
            if (node.isText) {
              const linkMark = node.marks.find((m: any) => m.type.name === 'link');
              if (linkMark && linkMark.attrs && linkMark.attrs.processing) {
                hasProcessingLink = true;
              }
            }
          });

          if (hasProcessingLink) {
            event.preventDefault();
            return true;
          }
        }
        return false;
      },
      handleClick(view, pos, event) {
        const target = event.target as HTMLElement;

        // Prevent interaction with processing links
        const processingAnchor = target.closest('a.gh-shortener-link-processing') as HTMLAnchorElement | null;
        if (processingAnchor) {
          event.preventDefault();
          event.stopPropagation();
          return true;
        }

        // Handle shortened link click - check both CSS class AND mark attributes
        // First check via mark attributes since CSS class might not be applied yet
        const $pos = view.state.doc.resolve(pos);
        const linkMark = $pos.marks().find((m) => m.type.name === 'link');
        
        // If mark has shortened=true, treat as shortened link regardless of CSS class
        if (linkMark && linkMark.attrs.shortened) {
          event.preventDefault();
          event.stopPropagation();
          const anchor = target.closest('a') as HTMLAnchorElement | null;
          if (anchor) {
            const rect = anchor.getBoundingClientRect();
            const originalUrl = linkMark.attrs.originalUrl || anchor.getAttribute('data-original-url') || '';
            if (originalUrl) {
              let from = pos;
              let to = pos;
              const markType = linkMark.type;
              while (from > 0 && markType.isInSet(view.state.doc.resolve(from - 1).marks())) {
                from--;
              }
              while (to < view.state.doc.content.size && markType.isInSet(view.state.doc.resolve(to).marks())) {
                to++;
              }
              setTimeout(() => {
                setShortenedOriginalUrl(originalUrl);
                setShortenedLinkPos(from);
                setShortenedLinkTo(to);
                setShortenedPopoverCoords({ x: rect.left + rect.width / 2 + window.scrollX, y: rect.top + window.scrollY });
                setShortenedPopoverOpen(true);
              }, 0);
            }
          }
          return true;
        }

        // Fallback: Handle shortened link click via CSS class
        const shortenedAnchor = target.closest('a.gh-shortened-link') as HTMLAnchorElement | null;
        if (shortenedAnchor) {
          event.preventDefault();
          event.stopPropagation();
          const rect = shortenedAnchor.getBoundingClientRect();
          const originalUrl = shortenedAnchor.getAttribute('data-original-url') || shortenedAnchor.getAttribute('data-href') || '';
          if (originalUrl && originalUrl !== 'javascript:void(0)') {
            const $pos = view.state.doc.resolve(pos);
            const linkMark = $pos.marks().find((m) => m.type.name === 'link');
            if (linkMark) {
              let from = pos;
              let to = pos;
              const markType = linkMark.type;
              while (from > 0 && markType.isInSet(view.state.doc.resolve(from - 1).marks())) {
                from--;
              }
              while (to < view.state.doc.content.size && markType.isInSet(view.state.doc.resolve(to).marks())) {
                to++;
              }
              setTimeout(() => {
                setShortenedOriginalUrl(originalUrl);
                setShortenedLinkPos(from);
                setShortenedLinkTo(to);
                setShortenedPopoverCoords({ x: rect.left + rect.width / 2 + window.scrollX, y: rect.top + window.scrollY });
                setShortenedPopoverOpen(true);
              }, 0);
            }
          }
          return true;
        }
        
        // Handle shortener link click
        const anchor = target.closest('a.gh-shortener-link') as HTMLAnchorElement | null;
        if (anchor) {
          event.preventDefault();
          event.stopPropagation();
          const rect = anchor.getBoundingClientRect();
          const url = anchor.getAttribute('data-href') || '';
          if (url && url !== 'javascript:void(0)') {
            const $pos = view.state.doc.resolve(pos);
            const linkMark = $pos.marks().find(m => m.type.name === 'link');
            if (linkMark) {
              let from = pos;
              let to = pos;
              const markType = linkMark.type;
              while (from > 0 && markType.isInSet(view.state.doc.resolve(from - 1).marks())) {
                from--;
              }
              while (to < view.state.doc.content.size && markType.isInSet(view.state.doc.resolve(to).marks())) {
                to++;
              }
              setTimeout(() => {
                setActiveLinkElement(anchor);
                setLinkPopoverUrl(url);
                setLinkPos(from);
                setLinkTo(to);
                setLinkPopoverCoords({ x: rect.left + rect.width / 2 + window.scrollX, y: rect.top + window.scrollY });
                setLinkPopoverOpen(true);
              }, 0);
            }
          }
          return true;
        }
        return false;
      },
      handleDOMEvents: {
        click(view, event) {
          const target = event.target as HTMLElement;
          const processingAnchor = target.closest('a.gh-shortener-link-processing') as HTMLAnchorElement | null;
          if (processingAnchor) {
            event.preventDefault();
            event.stopPropagation();
            return true;
          }
          const shortenedAnchor = target.closest('a.gh-shortened-link') as HTMLAnchorElement | null;
          if (shortenedAnchor) {
            event.preventDefault();
            event.stopPropagation();
            return true;
          }
          const anchor = target.closest('a.gh-shortener-link') as HTMLAnchorElement | null;
          if (anchor) {
            event.preventDefault();
            event.stopPropagation();
            return true;
          }
          return false;
        },
      },
    },
  });

  const { addExcludedUrl, isProcessing } = useLinkShortener({
    editor,
    excludedClearTrigger,
    onProcessingChange,
  });

  useEffect(() => {
    if (editor && editor.getText({ blockSeparator: '\n' }) !== message) {
      // Handle empty message
      if (message === '' || message.trim() === '') {
        editor.commands.setContent('<p></p>');
        return;
      }
      
      // Clear processed shortened URLs when content changes (e.g., new template selected)
      // This allows links to be auto-shortened again
      const storage = (editor.storage as any).link;
      if (storage) {
        storage.processedShortened?.clear();
      }
      
      // Convert plain text with newlines to proper HTML paragraphs
      const paragraphs = message.split('\n').filter(line => line.trim() !== '' || message.split('\n').length === 1);
      const htmlContent = paragraphs.length > 0 
        ? paragraphs.map(p => `<p>${p || '<br>'}</p>`).join('')
        : '<p></p>';
      
      editor.commands.setContent(htmlContent);
      // Trigger scan for links after template/message change
      setTimeout(() => {
        const storage = (editor.storage as any).link; // Storage is keyed by extension name ('link')
        if (storage && storage.scan) {
          storage.scan();
        }
      }, 300);
    }
  }, [message, editor]);

  useEffect(() => {
    if (editor) editor.setEditable(!!activeContact && !isSending);
  }, [activeContact, isSending, editor]);

  // Fallback: listen for document clicks on links in case editor handlers don't fire
  useEffect(() => {
    const handler = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target || !editor) return;

      // Block processing link interactions
      const processingAnchor = target.closest && (target.closest('a.gh-shortener-link-processing') as HTMLAnchorElement | null);
      if (processingAnchor) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      // Try to find any anchor element
      const anyAnchor = target.closest && (target.closest('a') as HTMLAnchorElement | null);
      if (!anyAnchor) return;

      // Get the ProseMirror position and check mark attributes
      try {
        const textNode = anyAnchor.firstChild;
        if (textNode && textNode.nodeType === Node.TEXT_NODE) {
          const pos = editor.view.posAtDOM(textNode, 0);
          const $pos = editor.view.state.doc.resolve(pos);
          const linkMark = $pos.marks().find((m: any) => m.type.name === 'link');
          
          // If mark has shortened=true, show shortened popup
          if (linkMark && linkMark.attrs.shortened) {
            event.preventDefault();
            event.stopPropagation();
            const rect = anyAnchor.getBoundingClientRect();
            const originalUrl = linkMark.attrs.originalUrl || anyAnchor.getAttribute('data-original-url') || '';
            if (originalUrl) {
              const from = editor.view.posAtDOM(textNode, 0);
              const to = editor.view.posAtDOM(textNode, textNode.textContent?.length || 0);
              setShortenedPopoverOpen(false);
              setTimeout(() => {
                setShortenedOriginalUrl(originalUrl);
                setShortenedLinkPos(from);
                setShortenedLinkTo(to);
                setShortenedPopoverCoords({ x: rect.left + rect.width / 2 + window.scrollX, y: rect.top + window.scrollY });
                setShortenedPopoverOpen(true);
              }, 0);
            }
            return;
          }
        }
      } catch (e) {
        // Position resolution failed, fall back to CSS class detection
      }

      // Fallback: Handle shortened link via CSS class
      const shortenedAnchor = target.closest && (target.closest('a.gh-shortened-link') as HTMLAnchorElement | null);
      if (shortenedAnchor) {
        event.preventDefault();
        event.stopPropagation();
        const rect = shortenedAnchor.getBoundingClientRect();
        const originalUrl = shortenedAnchor.getAttribute('data-original-url') || shortenedAnchor.getAttribute('data-href') || '';
        if (originalUrl && originalUrl !== 'javascript:void(0)') {
          const textNode = shortenedAnchor.firstChild;
          if (textNode && textNode.nodeType === Node.TEXT_NODE) {
            const from = editor.view.posAtDOM(textNode, 0);
            const to = editor.view.posAtDOM(textNode, textNode.textContent?.length || 0);
            setShortenedPopoverOpen(false);
            setTimeout(() => {
              setShortenedOriginalUrl(originalUrl);
              setShortenedLinkPos(from);
              setShortenedLinkTo(to);
              setShortenedPopoverCoords({ x: rect.left + rect.width / 2 + window.scrollX, y: rect.top + window.scrollY });
              setShortenedPopoverOpen(true);
            }, 0);
          }
        }
        return;
      }

      // Handle shortener link fallback
      const anchor = target.closest && (target.closest('a.gh-shortener-link') as HTMLAnchorElement | null);
      if (anchor && editor) {
        event.preventDefault();
        event.stopPropagation();
        const rect = anchor.getBoundingClientRect();
        const url = anchor.getAttribute('data-href') || '';
        if (url && url !== 'javascript:void(0)') {
          // Find the ProseMirror position from the text node inside the anchor
          const textNode = anchor.firstChild;
          if (textNode && textNode.nodeType === Node.TEXT_NODE) {
            const from = editor.view.posAtDOM(textNode, 0);
            const to = editor.view.posAtDOM(textNode, textNode.textContent?.length || 0);
            // Close existing popover first, then open new one to ensure proper re-render
            setLinkPopoverOpen(false);
            setTimeout(() => {
              setActiveLinkElement(anchor);
              setLinkPopoverUrl(url);
              setLinkPos(from);
              setLinkTo(to);
              setLinkPopoverCoords({ x: rect.left + rect.width / 2 + window.scrollX, y: rect.top + window.scrollY });
              setLinkPopoverOpen(true);
            }, 0);
          }
        }
      }
    };

    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [editor]);

  return (
    <>
      {shortenedPopoverCoords && (
        <Popover
          open={shortenedPopoverOpen}
          content={
            <div style={{ padding: 0, maxWidth: 300 }}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>Original URL:</div>
              <div style={{ marginBottom: 12, wordBreak: 'break-all', fontSize: 12, color: '#666' }}>
                {shortenedOriginalUrl}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  size="small"
                  onClick={() => {
                    if (editor && shortenedLinkPos !== null && shortenedLinkTo !== null) {
                      const { state, view } = editor;
                      const tr = state.tr.replaceWith(
                        shortenedLinkPos,
                        shortenedLinkTo,
                        state.schema.text(shortenedOriginalUrl, [
                          state.schema.marks.link.create({ href: shortenedOriginalUrl, shortened: false, originalUrl: null, processing: false })
                        ])
                      );
                      view.dispatch(tr);
                      // Remember this URL so it won't be auto-shortened again
                      addExcludedUrl(shortenedOriginalUrl);
                    }
                    setShortenedPopoverOpen(false);
                  }}
                >
                  Revert to Original
                </Button>
                <Button size="small" onClick={() => setShortenedPopoverOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          }
          placement="top"
          trigger={[]}
          getPopupContainer={() => document.body}
        >
          <span
            style={{
              position: 'fixed',
              left: shortenedPopoverCoords.x,
              top: shortenedPopoverCoords.y,
              width: 1,
              height: 1,
              zIndex: 9999,
              pointerEvents: 'auto',
            }}
          />
        </Popover>
      )}

      {linkPopoverCoords && (
        <Popover
          open={linkPopoverOpen}
          content={
            shorteningLoading ? (
              <div style={{ padding: 5 }}>Shortening URL...</div>
            ) : (
              <div style={{ padding: 0 }}>
                <div style={{ marginBottom: 8 }}>Do you want to shorten this URL?</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button
                    size="small"
                    onClick={async () => {
                      setShorteningLoading(true);
                      try {
                        const short = await shortenUrlFromStore(linkPopoverUrl);
                        if (editor && linkPos !== null && linkTo !== null) {
                          const { state, view } = editor;
                          const displayText = short || linkPopoverUrl;
                          const href = short || linkPopoverUrl;
                          const tr = state.tr.replaceWith(
                            linkPos,
                            linkTo,
                            state.schema.text(displayText, [
                              state.schema.marks.link.create({ href, shortened: !!short, originalUrl: linkPopoverUrl, processing: false })
                            ])
                          );
                          view.dispatch(tr);
                        }
                      } finally {
                        setShorteningLoading(false);
                        setLinkPopoverOpen(false);
                      }
                    }}
                  >
                    Yes
                  </Button>
                  <Button size="small" onClick={() => {
                    // Remember this URL so it won't be auto-shortened again
                    addExcludedUrl(linkPopoverUrl);
                    setLinkPopoverOpen(false);
                  }}>
                    No
                  </Button>
                </div>
              </div>
            )
          }
          placement="top"
          trigger={[]}
          getPopupContainer={() => document.body}
        >
          <span
            ref={popupAnchorRef}
            style={{
              position: 'fixed',
              left: linkPopoverCoords.x,
              top: linkPopoverCoords.y,
              width: 1,
              height: 1,
              zIndex: 9999,
              pointerEvents: 'auto',
            }}
          />
        </Popover>
      )}

      <EditorContent
        editor={editor}
        style={{
          width: '100%',
          fontWeight: 'normal',
          border: 'none',
          outline: 'none',
          boxShadow: 'none',
          flex: 1,
          minHeight: '40px',
          maxHeight: '200px',
          overflowY: 'auto',
          lineHeight: '1.5',
          padding: '4px 8px',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
        }}
      />
    </>
  );
};

export default MessageRichEditor;
